import { useMemo, useState } from 'react';
import {
  Page,
  Card,
  IndexTable,
  Filters,
  Text,
  InlineStack,
  Box,
  Spinner,
  Banner,
} from '@shopify/polaris';
import { useQuery } from '@tanstack/react-query';
import { getVehicleFilterOptions } from '../api/vehicles';

export function BoltPatternsList() {
  const [query, setQuery] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['vehicle-filter-options', null],
    queryFn: () => getVehicleFilterOptions(),
    staleTime: 60_000,
  });

  const patterns = useMemo(() => {
    const all = data?.bolt_patterns ?? [];
    const q = query.trim().toLowerCase();
    const filtered = q
      ? all.filter((p) => p.bolt_pattern_canonical.toLowerCase().includes(q))
      : all;
    return [...filtered].sort((a, b) => b.count - a.count);
  }, [data, query]);

  const totalVehicles = patterns.reduce((s, p) => s + p.count, 0);

  return (
    <Page
      title="Bolt patterns indexed"
      subtitle="Distinct canonical bolt patterns across all vehicles in the catalog"
    >
      <Card padding="0">
        <Box padding="400" borderBlockEndWidth="025" borderColor="border">
          <Filters
            queryValue={query}
            queryPlaceholder="Search bolt pattern (e.g. 5x114.3)"
            onQueryChange={setQuery}
            onQueryClear={() => setQuery('')}
            onClearAll={() => setQuery('')}
            filters={[]}
          />
        </Box>
        {error && (
          <Box padding="400">
            <Banner tone="critical" title="Failed to load bolt patterns">{(error as Error).message}</Banner>
          </Box>
        )}
        {isLoading ? (
          <Box padding="600">
            <InlineStack align="center"><Spinner size="small" /></InlineStack>
          </Box>
        ) : (
          <IndexTable
            resourceName={{ singular: 'bolt pattern', plural: 'bolt patterns' }}
            itemCount={patterns.length}
            selectable={false}
            headings={[
              { title: 'Canonical key' },
              { title: 'Vehicles', alignment: 'end' },
            ]}
          >
            {patterns.map((p, i) => (
              <IndexTable.Row id={p.bolt_pattern_canonical} key={p.bolt_pattern_canonical} position={i}>
                <IndexTable.Cell>
                  <Text as="span" variant="bodyMd" fontWeight="semibold">
                    {p.bolt_pattern_canonical}
                  </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Text as="span" variant="bodyMd" alignment="end">
                    {p.count.toLocaleString()}
                  </Text>
                </IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
        )}
        {!isLoading && !error && (
          <Box padding="400" borderBlockStartWidth="025" borderColor="border">
            <Text as="p" variant="bodySm" tone="subdued">
              {patterns.length.toLocaleString()} {patterns.length === 1 ? 'pattern' : 'patterns'}
              {' · '}
              {totalVehicles.toLocaleString()} vehicles total
            </Text>
          </Box>
        )}
      </Card>
    </Page>
  );
}
